import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

// Extend dayjs with quarter plugin
dayjs.extend(quarterOfYear);

/**
 * Helper functions for quarter calculations
 */
const getQuarterRange = (date) => {
  const start = date.startOf('quarter');
  const end = date.endOf('quarter');
  return [start, end];
};

const getPreviousQuarterRange = (date) => {
  const prevQuarterDate = date.subtract(1, 'quarter');
  const start = prevQuarterDate.startOf('quarter');
  const end = prevQuarterDate.endOf('quarter');
  return [start, end];
};

/**
 * Custom hook for date picker logic including presets and change handling
 * @param {Function} setStartDate - Function to set start date
 * @param {Function} setEndDate - Function to set end date
 * @returns {Object} Date picker configuration and handlers
 */
export const useDatePicker = (setStartDate, setEndDate) => {
  // Memoize range presets to prevent recreation on every render
  const rangePresets = useMemo(() => [
    { label: 'This month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
    { label: 'This quarter', value: getQuarterRange(dayjs()) },
    { label: 'This year', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
    { label: 'Previous month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
    { label: 'Previous quarter', value: getPreviousQuarterRange(dayjs()) },
    { label: 'Previous year', value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')] },
    { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
    { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
    { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
    { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
    // Commented out monthly presets for future use
    // { label: 'January', value: [dayjs().startOf('year').month(0), dayjs().startOf('year').month(0).endOf('month')] },
    // { label: 'February', value: [dayjs().startOf('year').month(1), dayjs().startOf('year').month(1).endOf('month')] },
    // { label: 'March', value: [dayjs().startOf('year').month(2), dayjs().startOf('year').month(2).endOf('month')] },
    // { label: 'April', value: [dayjs().startOf('year').month(3), dayjs().startOf('year').month(3).endOf('month')] },
    // { label: 'May', value: [dayjs().startOf('year').month(4), dayjs().startOf('year').month(4).endOf('month')] },
    // { label: 'June', value: [dayjs().startOf('year').month(5), dayjs().startOf('year').month(5).endOf('month')] },
    // { label: 'July', value: [dayjs().startOf('year').month(6), dayjs().startOf('year').month(6).endOf('month')] },
    // { label: 'August', value: [dayjs().startOf('year').month(7), dayjs().startOf('year').month(7).endOf('month')] },
    // { label: 'September', value: [dayjs().startOf('year').month(8), dayjs().startOf('year').month(8).endOf('month')] },
    // { label: 'October', value: [dayjs().startOf('year').month(9), dayjs().startOf('year').month(9).endOf('month')] },
    // { label: 'November', value: [dayjs().startOf('year').month(10), dayjs().startOf('year').month(10).endOf('month')] },
    // { label: 'December', value: [dayjs().startOf('year').month(11), dayjs().startOf('year').month(11).endOf('month')] },
  ], []);

  // Memoize the handleDateChange function
  const handleDateChange = useCallback((e) => {
    // Use dayjs objects directly
    const startDayjs = e[0];
    const endDayjs = e[1];
    
    setStartDate(startDayjs);
    setEndDate(endDayjs);
  }, [setStartDate, setEndDate]);

  return {
    rangePresets,
    handleDateChange
  };
};
